import { format } from 'date-fns'
import { getBulkUpdatePath } from '../Components/BulkUpdate/BulkUpdateHelper'
import CONSTANTS from '../Constants'
import { API_CONFIG, API_LIST } from '../Constants/ApiDefinations'
import { CONFIG } from '../Constants/Constant/Configs'
import { PAGE_URL, PageNames } from '../Constants/PagesRoutes/PageURLs'
import { TypeObject } from '../Models'
import mainApiService from '../Services'
import { showAlert } from '../Store/Slice/alert.slice'
import { setAssetLangVariants } from '../Store/Slice/asset-lang-variant.slice'
import { setLoader } from '../Store/Slice/loader.slice'
import { PERMISSIONS, checkPermission } from './Permission'
import {
  combineErrorMessage,
  downloadDataInCSV,
  getSelectedAttributeDetails,
  sanitizeFileName,
} from './Util'

const cuePointStaticAttrArr = [
  'STRUCTURED_CUE_POINTS',
  'STRUCTURED_AD_CUE_POINTS',
  'STRUCTURED_ADVISORY_CUE_POINTS',
]
const advisoryCuePointHeaders = [
  'Blitz ID',
  'Advisory Cue Point Type',
  'Advisory Cue Point Start Time',
  'Advisory Cue Point End Time',
]
const adCuePointHeaders = ['Blitz ID', 'Cue Point Start Time', 'Adformat', 'Class']
const liveAGStaticAttrArr = ['SRT_FEED', 'ENCODED_FEED', 'SRT_FEED_GEC', 'ENCODED_FEED_GEC']

export const removeSelectedAttributeDetails = (newValues: any, selectedAttributes: any) => {
  const notPresentInNewValues = selectedAttributes.filter(
    (attribute: TypeObject) =>
      !newValues.some((newValue: TypeObject) => newValue.fieldName === attribute.fieldName),
  )

  const matchingObjects: TypeObject[] = [...newValues]

  for (const attr of notPresentInNewValues) {
    if (attr?.attributeRef?.length) {
      for (const attRef of attr.attributeRef) {
        const matchingDataObjectIdx = matchingObjects.findIndex(
          (ele: any) => ele._id === attRef._id,
        )
        matchingObjects.splice(matchingDataObjectIdx, 1)
      }
    }

    matchingObjects.forEach((ele: TypeObject, idx: number) => {
      if (ele?.attributeRef?.length && ele?.fieldType === CONSTANTS.FIELDTYPES.COMPLEXTYPE) {
        const attrefEle = ele.attributeRef.find((item: any) => item._id === attr._id)
        if (attrefEle) {
          matchingObjects[idx].attributeRef.forEach((matchObj: TypeObject) => {
            const matchInMatchObjectIdx = matchingObjects.findIndex(
              (item: any) => item._id === matchObj._id,
            )
            matchingObjects.splice(matchInMatchObjectIdx, 1)
          })
          matchingObjects.splice(idx, 1)
        }
      }
    })
  }

  return matchingObjects
}

export const handleAttributesCreateJob = (
  newValues: TypeObject[],
  reason: string,
  attributes: TypeObject[],
  selectedAttributes: TypeObject[],
) => {
  if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.INPUT) {
    return
  }
  if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.CLEAR) {
    return { selectedAttributes: [], disabledLabel: [] }
  }
  if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.SELECTOPTION) {
    const copyArr = [...newValues]
    const latestAttribute = copyArr[copyArr.length - 1]
    if (
      latestAttribute?.fieldType === CONSTANTS.FIELDTYPES.COMPLEXTYPE &&
      latestAttribute?.attributeRef?.length
    ) {
      copyArr.push(...getSelectedAttributeDetails(latestAttribute.attributeRef, attributes))
    }
    const disabledLabels = copyArr.map((item: TypeObject) => item.label)

    return { selectedAttributes: copyArr, disabledLabel: disabledLabels }
  }
  if (reason === CONSTANTS.AUTOCOMPLETE_EVENT_TYPES.REMOVEOPTION) {
    const copyArr: TypeObject[] = []
    const copySelectedAttr: TypeObject[] = [...selectedAttributes]
    copyArr.push(...removeSelectedAttributeDetails(newValues, copySelectedAttr))
    const disabledLabels = copyArr.map((item: TypeObject) => item.label)
    return { selectedAttributes: copyArr, disabledLabel: disabledLabels }
  }
  return { selectedAttributes: [], disabledLabel: [] }
}

export const getDisabledLabels = (attributes: TypeObject[], disabledLabels: string[]) => {
  const complexTypeAttributes: TypeObject[] = attributes.filter(
    (item: TypeObject) => item.fieldType === CONSTANTS.FIELDTYPES.COMPLEXTYPE,
  )
  const disabledAttrSet = new Set([...disabledLabels])
  for (const attr of complexTypeAttributes) {
    for (const attRef of attr.attributeRef) {
      const matchingAttr = attributes.find((item: TypeObject) => item?._id === attRef._id)
      if (matchingAttr) {
        disabledAttrSet.add(matchingAttr?.label as string)
      }
    }
  }

  return Array.from(disabledAttrSet)
}

export const checkPermissionsBulkUpload = (
  permissionType: string[],
  pageType?: string | undefined,
  navigate?: any,
) => {
  const permission: any = PERMISSIONS
  const permissionKey =
    permission[String(pageType).replaceAll('-', '_').toUpperCase()] || PERMISSIONS.BULK_UPDATE
  const accessWrite = checkPermission(permissionKey, permissionType)

  if (!accessWrite) {
    navigate('/access-denied')
  }
}

export const getDataOfCsvBasedOnHeaders = (csvData: TypeObject[], jobDetails: TypeObject) => {
  const excludedFields = [...CONSTANTS.BULK_UPDATE_EXCLUDED_ATTRIBUTE].map((item) =>
    item.toLowerCase(),
  )
  const attributesAvailableInComplexFields = getComplexAttributesFromJobDetails(jobDetails)

  const keysOfCsvHeaders = Object.keys(csvData?.[0] ?? {})?.filter((header) => !!header)

  const notPresentAsPerData: string[] = []

  if (keysOfCsvHeaders?.length) {
    const attributesMap = new Set(
      jobDetails?.attributes?.map((attr: any) => attr.label.toLowerCase()),
    )

    for (const key of keysOfCsvHeaders) {
      if (
        !attributesMap.has(key?.trim()?.toLowerCase()) &&
        !attributesAvailableInComplexFields.has(key?.trim()?.toLowerCase()) &&
        !excludedFields.includes(key?.toLowerCase())
      ) {
        notPresentAsPerData.push(key?.trim()?.toLowerCase())
      }
    }
    return notPresentAsPerData
  }

  return notPresentAsPerData
}

const generateJobDrawerRequestData = ({
  currentPage,
  jobInfo,
  jobData,
  submitData,
}: {
  currentPage: string
  jobInfo?: any
  jobData?: {
    jobName: string
    selectedAttributes?: any
    selectedAction?: string | any[]
    copyBaseImages?: boolean
    applicableAttributes?: any[]
    isLiveAssetJob?: boolean
    jobSubType?: any | undefined
  }
  submitData?: any
}): any => {
  const isEdit = !!(jobInfo?.jobId && jobInfo?.jobName)
  const isBulkUpdatePage = currentPage === PAGE_URL[PageNames.BlukUpdate]
  const isBulkStateChangePage = currentPage === PAGE_URL[PageNames.BulkStateChange]
  const isInteractiveBulkUpdatePage = currentPage === PAGE_URL[PageNames.InteractiveBulkUpdate]
  const isBulkTranscodePage = currentPage === PAGE_URL[PageNames.BulkTranscoding]

  if (
    isBulkUpdatePage ||
    isBulkStateChangePage ||
    isInteractiveBulkUpdatePage ||
    isBulkTranscodePage
  ) {
    let reqData: any = { ...(isEdit ? { jobId: jobInfo.jobId } : {}) }
    if (submitData) {
      reqData = submitData
    } else {
      if (jobInfo?.jobName !== jobData?.jobName) {
        reqData.jobName = jobData?.jobName.trim()
      }
      if (isBulkUpdatePage) {
        reqData.jobType = CONFIG.BULK_OPS_TYPES.BULK_UPDATE
        reqData.attributes = jobData?.selectedAttributes
        reqData.jobType = CONFIG.BULK_OPS_TYPES.BULK_UPDATE
        reqData.isLiveAssetJob = !!(jobData?.isLiveAssetJob || jobInfo?.isLiveAssetJob)
        reqData.jobSubType = jobData?.jobSubType || undefined
        if (
          reqData.isLiveAssetJob &&
          !liveAGStaticAttrArr.includes(jobData?.selectedAttributes[0].key)
        ) {
          reqData.isLiveAssetJob = false
        }
      } else if (isBulkStateChangePage) {
        reqData.jobType = CONFIG.BULK_OPS_TYPES.BULK_STATE_CHANGE
        reqData.requestData = { targetState: jobData?.selectedAction }
      } else if (isInteractiveBulkUpdatePage) {
        const applicableAttributes = jobData?.applicableAttributes?.map((i: any) => {
          let value = i?.selectedValueType
          if (i?.selectedAttribute?.fieldType === CONSTANTS.FIELDTYPES.SELECT) {
            if (i?.selectedAttribute?.isMultiple) {
              value = i?.selectedValueType?.map((val: any) => val?.key)?.join(', ')
            } else {
              if (i?.selectedAttribute.fieldName === 'packageId') {
                value = i?.selectedValueType?.value || i.selectedValueType
              } else {
                value = i?.selectedValueType?.key || i.selectedValueType
              }
            }
          } else if (
            i?.selectedValueType &&
            i?.selectedAttribute?.fieldType === CONSTANTS.FIELDTYPES.NUMBER &&
            i?.selectedAttribute?.dataType === CONSTANTS.FIELDTYPES.NUMBER
          ) {
            value = Number(i?.selectedValueType)
          } else if (
            i?.selectedValueType &&
            i?.selectedAttribute?.fieldType !== CONSTANTS.FIELDTYPES.NUMBER &&
            [
              CONSTANTS.FIELDTYPES.DATE,
              CONSTANTS.FIELDTYPES.TIME,
              CONSTANTS.FIELDTYPES.DATETIME,
            ].includes(i?.selectedAttribute?.fieldType)
          ) {
            value = format(new Date(i.selectedValueType), 'dd/MM/yyyy HH:mm')
          }
          return {
            fieldName: i?.selectedAttribute?.fieldName,
            fieldLabel: i?.selectedAttribute?.label,
            updateBehaviour: i?.selectedUpdateType?.key,
            value: value || null,
          }
        })
        reqData.jobType = CONFIG.BULK_OPS_TYPES.INTERACTIVE_BULK_UPDATE
        reqData.requestData = { attributes: applicableAttributes }
      } else if (isBulkTranscodePage) {
        reqData.jobType = 'BULK_UPDATE_TRANSCODING'
        reqData.requestData = { processType: jobData?.selectedAction }
      }
    }
    // if (isEdit) {
    //   return API_CONFIG(API_LIST.BULK_UPLOAD.UPDATE_JOB, reqData, {
    //     ...jobInfo,
    //     jobType: getBulkUpdatePath(jobInfo.jobType),
    //   })
    // } else {
    //   return API_CONFIG(API_LIST.BULK_UPLOAD.ADD_JOB, reqData, {
    //     jobType: getBulkUpdatePath(reqData?.jobType),
    //   })
    // }

    if (isEdit) {
      if (isBulkTranscodePage) {
        const requestedData: any = API_CONFIG(API_LIST.BULK_APIS.TRANSCODING_JOB_UPDATE, reqData, {
          transcodingJobId: reqData?.jobId,
        })
        return requestedData
      }
      return API_CONFIG(API_LIST.BULK_UPLOAD.UPDATE_JOB, reqData, {
        ...jobInfo,
        jobType: getBulkUpdatePath(jobInfo.jobType),
      })
    }
    if (isBulkTranscodePage) {
      const requestedData: any = API_CONFIG(API_LIST.BULK_APIS.ADD_TRANSCODING_JOB, reqData)
      return requestedData
    }
    return API_CONFIG(API_LIST.BULK_UPLOAD.ADD_JOB, reqData, {
      jobType: getBulkUpdatePath(reqData?.jobType),
    })
  }

  if (currentPage === '/image-variant') {
    if (Array.isArray(jobData?.selectedAction)) {
      const clonePayload = jobData?.selectedAction.map((lang: any) => {
        return API_CONFIG(API_LIST.CONTENT.CLONE_ASSET, {
          contentId: jobInfo?._id,
          copies: 1,
          variantLang: lang,
          createVariant: true,
          baseContentId: jobInfo?.contentId,
          cloneImage: jobData?.copyBaseImages,
        })
      })
      return clonePayload
    } else {
      return API_CONFIG(API_LIST.CONTENT.CLONE_ASSET, {
        contentId: jobInfo?._id,
        copies: 1,
        variantLang: jobData?.selectedAction,
        createVariant: true,
        baseContentId: jobInfo?.contentId,
        cloneImage: jobData?.copyBaseImages,
      })
    }
  }
  return API_CONFIG(API_LIST.MANAGE_FILE.ADD, {
    jobType: jobData?.selectedAction,
    jobName: jobData?.jobName?.trim(),
  })
}

async function handleCreateOrUpdateJobResponse(
  result: any,
  currentPage: any,
  setErrors: any,
  dispatch: any,
  handleClose: any,
  resetForm: any,
  submitData: any,
  navigate: any,
  jobInfo: any,
  languagesMastersData: any,
  isFromEditor: boolean | undefined,
) {
  if (dispatch && currentPage !== '/image-variant') {
    dispatch(setLoader(false))
  }
  if (result?.error) {
    const blitzCode = result.error.blitzCode
    if (
      setErrors &&
      (blitzCode === CONSTANTS.BLITZ_JOB_EXIST_ERROR_CODE ||
        blitzCode === CONSTANTS.BLITZ_JOB_EXIST_ERROR_CODE_MANAGEFILE)
    ) {
      setErrors((prev: any) => ({ ...prev, jobName: { message: result.error.message } }))
    } else if (dispatch) {
      dispatch(showAlert([true, combineErrorMessage(result), 'error']))
    }
    if (blitzCode === 'BLITZ_21013') handleClose(false)
    return
  }

  handleClose(false)
  resetForm()
  dispatch && dispatch(setLoader(false))
  const pageType = submitData ? 'view' : 'edit'
  const isBulkUpdatePage = currentPage === PAGE_URL[PageNames.BlukUpdate]
  const isBulkStateChangePage = currentPage === PAGE_URL[PageNames.BulkStateChange]
  const isInteractiveBulkUpdatePage = currentPage === PAGE_URL[PageNames.InteractiveBulkUpdate]
  const isBulkTranscodePage = currentPage === PAGE_URL[PageNames.BulkTranscoding]

  if (isBulkUpdatePage) {
    navigate(`${PAGE_URL[PageNames.BlukUpdate]}/${pageType}/${result?.data?.jobId}`)
  } else if (isBulkStateChangePage) {
    dispatch &&
      dispatch(
        showAlert([
          true,
          result?.message === 'Bulk update started' && result?.data?.requestData?.blitzIds?.length
            ? CONSTANTS.BULK_STATE_SUCCESS
            : result?.message === 'Bulk update started' && isFromEditor
              ? 'Bulk updated successfully.'
              : result?.message,
          'success',
        ]),
      )
    navigate(`${PAGE_URL[PageNames.BulkStateChange]}/${pageType}/${result?.data?.jobId}`)
  } else if (isInteractiveBulkUpdatePage) {
    dispatch &&
      dispatch(
        showAlert([
          true,
          result?.message === 'Bulk update started' && isFromEditor
            ? 'Bulk updated successfully.'
            : result?.message,
          'success',
        ]),
      )
    navigate(`${PAGE_URL[PageNames.InteractiveBulkUpdate]}/${pageType}/${result?.data?.jobId}`)
  } else if (isBulkTranscodePage) {
    let message = result?.message
    if (result?.message === 'Bulk update started') {
      message = isFromEditor
        ? 'Bulk re-transcoding updated successfully.'
        : 'Bulk re-transcoding started.'
    } else if (result?.message === 'Bulk update scheduled successfully') {
      message = 'Bulk re-transcoding scheduled successfully.'
    }
    dispatch && dispatch(showAlert([true, message, 'success']))
    navigate(`${PAGE_URL[PageNames.BulkTranscoding]}/${pageType}/${result?.data?.jobId}`)
  } else if (currentPage === '/manage-files') {
    navigate(`/manage-files/edit/${result?.data?.jobId}`)
  } else if (currentPage === '/image-variant') {
    if (dispatch) {
      dispatch(showAlert([true, result?.message, 'success']))
    }

    const viewStatusRequest: any = API_CONFIG(
      API_LIST.CONTENT.VIEW_MARK_DONE_STATUS,
      {},
      { contentId: jobInfo?._id },
    )
    const res = await mainApiService(viewStatusRequest)
    if (dispatch) {
      dispatch(setLoader(false))
    }
    if (res?.data && dispatch) {
      dispatch(
        setAssetLangVariants({
          ...(res.data || {}),
          metaMaster: 'CONTENT_MEDIA_IMAGE',
          contentId: jobInfo._id,
          languagesMastersData,
        }),
      )
      navigate(
        `/asset/view/${jobInfo._id}/${jobInfo.assetType}/${jobInfo.contentCategory}/${jobInfo.assetCategory}/CONTENT_MEDIA_IMAGE?langVariantId=${result?.data?.[0]?._id}`,
      )
    }
  }
}
export const createOrUpdateJob = async ({
  currentPage,
  jobInfo,
  jobData,
  submitData,
  setErrors,
  dispatch,
  handleClose,
  resetForm,
  navigate,
  languagesMastersData,
  isFromEditor,
}: {
  currentPage: string
  jobInfo: any
  jobData?: {
    jobName: string
    selectedAttributes?: any
    selectedAction?: string | any[]
    copyBaseImages?: boolean
    applicableAttributes?: any[]
    isLiveAssetJob?: boolean
    jobSubType?: any | undefined
  }
  submitData?: any
  languagesMastersData?: any
  setErrors?: (cb: (prev: any) => any) => void
  dispatch?: (action: any) => void
  handleClose: (...args: any[]) => void
  resetForm: () => void
  navigate: (url: string) => void
  isFromEditor?: boolean
}): Promise<void> => {
  if (dispatch) {
    dispatch(setLoader(true))
  }
  const reqData = generateJobDrawerRequestData({ currentPage, jobInfo, jobData, submitData })
  if (currentPage === '/image-variant' && Array.isArray(reqData)) {
    const imageResult = await Promise.all(reqData.map((data) => mainApiService(data)))
    const successfulCalls = imageResult?.filter((res: any) => res.responseCode === 200)
    const failedCalls = imageResult?.filter((res: any) => res.responseCode !== 200)
    if (successfulCalls?.length && failedCalls?.length) {
      dispatch &&
        dispatch(
          showAlert([
            true,
            `Out of ${reqData?.length} Variants ${successfulCalls?.length} is successfully created.`,
            'success',
          ]),
        )
      handleClose(false)
      resetForm()
      dispatch && dispatch(setLoader(false))
    } else if (imageResult[0]?.error) {
      dispatch && dispatch(showAlert([true, imageResult[0]?.message, 'error']))
      handleClose(false)
      resetForm()
      dispatch && dispatch(setLoader(false))
    } else {
      handleClose(false)
      resetForm()
      dispatch && dispatch(setLoader(false))
      if (dispatch) {
        dispatch(showAlert([true, imageResult[0]?.message, 'success']))
      }
      const viewStatusRequest: any = API_CONFIG(
        API_LIST.CONTENT.VIEW_MARK_DONE_STATUS,
        {},
        { contentId: jobInfo?._id },
      )

      const res = await mainApiService(viewStatusRequest)
      if (dispatch) {
        dispatch(setLoader(false))
      }
      if (res?.data && dispatch) {
        dispatch(
          setAssetLangVariants({
            ...(res.data || {}),
            metaMaster: 'CONTENT_MEDIA_IMAGE',
            contentId: jobInfo._id,
            languagesMastersData,
          }),
        )
        navigate(
          `/asset/view/${jobInfo._id}/${jobInfo.assetType}/${jobInfo.contentCategory}/${jobInfo.assetCategory}/CONTENT_MEDIA_IMAGE?langVariantId=${imageResult[0]?.data?.[0]?._id}`,
        )
      }
    }
  } else {
    const result = await mainApiService(reqData)
    handleCreateOrUpdateJobResponse(
      result,
      currentPage,
      setErrors,
      dispatch,
      handleClose,
      resetForm,
      submitData,
      navigate,
      jobInfo,
      languagesMastersData,
      isFromEditor,
    )
  }
}

const getComplexAttributesFromJobDetails = (jobDetails: TypeObject) => {
  const complexTypeAttributes: TypeObject[] =
    jobDetails?.attributes?.filter(
      (item: TypeObject) => item.fieldType === CONSTANTS.FIELDTYPES.COMPLEXTYPE,
    ) ?? []

  const attributesAvailableInComplexFields = new Set()

  for (const attr of complexTypeAttributes) {
    for (const attRef of attr.attributeRef) {
      const matchingAttr = jobDetails?.attributes?.find(
        (item: TypeObject) => item?._id === attRef._id,
      )
      if (matchingAttr) {
        attributesAvailableInComplexFields.add(matchingAttr?.label as string)
      }
    }
  }

  return attributesAvailableInComplexFields
}

export const fileValidation = async (
  csvData: TypeObject[],
  jobDetails: TypeObject,
  fileInfo: TypeObject,
  handleDialog: any,
  handleCloseDialog: any,
  bulkUpdateConfig: any,
  templateData: any,
) => {
  const showError = (msg: string) => {
    handleDialog(
      CONSTANTS.UPLOAD_FAILED_HEADING,
      msg,
      handleCloseDialog,
      null,
      'error',
      'DeleteRoleIcon',
    )
  }

  // 1. File type check
  if (fileInfo?.type !== 'text/csv') {
    showError('Incorrect file format. Only CSV files are allowed.')
    return false
  }

  // 2. Filter records
  const blitzKey = CONSTANTS.BULK_UPDATE_ATTRIBUTE_LABEL.BLITZ_ID

  const csvDataFile = csvData?.filter((item) => item[blitzKey])
  if (!csvDataFile?.length) {
    showError('No records found.')
    return false
  }

  if (
    jobDetails?.jobType === 'BULK_UPDATE_TRANSCODING' ||
    (jobDetails?.attributes &&
      !cuePointStaticAttrArr.includes(jobDetails?.jobSubType) &&
      !cuePointStaticAttrArr.includes(jobDetails?.attributes[0]?.fieldName))
  ) {
    // 3. Duplicate check
    const seen = new Set()
    const duplicates = new Set<string>()
    for (const row of csvDataFile) {
      const id = row[blitzKey]
      if (seen.has(id)) duplicates.add(id)
      seen.add(id)
    }
    if (duplicates.size) {
      showError(`Duplicate records found for Blitz Id: ${[...duplicates].join(', ')}`)
      return false
    }
  }

  // 4. Header validation
  const filteredCsvHeaders = Object.keys(csvData?.[0] ?? {}).filter(Boolean)
  if (csvData.some((row) => row.inputType === 'SRT-PARTNERFEED')) {
    showError('Input type SRT-PARTNERFEED is not allowed.')
    return false
  }
  if (
    ['scteType', 'sourceType', 'partner', 'audioPid', 'adPrefix'].some((field) =>
      filteredCsvHeaders.includes(field),
    )
  ) {
    showError('CSV must not contain scteType, sourceType, partner, audioPid, or adPrefix.')
    return false
  }

  let jobAttributesLength: number
  let templateHeaders: string[] | undefined

  if (jobDetails?.isLiveAssetJob) {
    const templateHeaderMap: Record<string, string[] | undefined> = {
      SRT_FEED: templateData?.srtData?.headers,
      ENCODED_FEED: templateData?.encodedData?.headers,
      SRT_FEED_GEC: templateData?.srtGecData?.headers,
      ENCODED_FEED_GEC: templateData?.encodedGecData?.headers,
    }
    templateHeaders = templateHeaderMap[jobDetails?.attributes?.[0]?.key] ?? []
    jobAttributesLength = templateHeaders?.length ?? 0
  } else if (jobDetails?.jobType === CONFIG.BULK_OPS_TYPES.BULK_TRANSCODING) {
    templateHeaders = templateData?.integratedData?.headers
    jobAttributesLength = templateHeaders?.length ?? 0
  } else {
    const attributesAvailableInComplexFields = getComplexAttributesFromJobDetails(jobDetails)
    const optionalCount = CONSTANTS.OPTIONAL_ATTRIBUTES_BULK_UPDATE.filter(
      (opt) => !csvData?.[0]?.[opt],
    ).length
    jobAttributesLength =
      jobDetails?.attributes?.length -
      attributesAvailableInComplexFields.size +
      CONSTANTS.BULK_UPDATE_EXCLUDED_ATTRIBUTE.length -
      optionalCount
  }

  let headersInvalid = null
  if (
    filteredCsvHeaders?.length === 1 &&
    jobDetails?.attributes?.length === 1 &&
    jobDetails?.attributes[0]?.fieldName === 'whaleOS'
  ) {
    headersInvalid = false
  } else if (
    jobDetails?.attributes?.length === 1 &&
    jobDetails?.attributes[0]?.fieldName === 'STRUCTURED_CUE_POINTS' &&
    filteredCsvHeaders.length === 6
  ) {
    headersInvalid = false
  } else if (
    jobDetails?.attributes?.length === 1 &&
    jobDetails?.attributes[0]?.fieldName === 'STRUCTURED_AD_CUE_POINTS' &&
    filteredCsvHeaders?.length === adCuePointHeaders.length &&
    adCuePointHeaders.every((header, idx) => filteredCsvHeaders[idx] === header)
  ) {
    headersInvalid = false
  } else if (
    jobDetails?.attributes?.length === 1 &&
    jobDetails?.attributes[0]?.fieldName === 'STRUCTURED_ADVISORY_CUE_POINTS' &&
    filteredCsvHeaders?.length === advisoryCuePointHeaders.length &&
    advisoryCuePointHeaders.every((header, idx) => filteredCsvHeaders[idx] === header)
  ) {
    headersInvalid = false
  } else {
    headersInvalid =
      jobAttributesLength !== filteredCsvHeaders?.length ||
      (jobDetails?.isLiveAssetJob || jobDetails?.jobType === CONFIG.BULK_OPS_TYPES.BULK_TRANSCODING
        ? templateHeaders?.some((h, idx) => filteredCsvHeaders[idx] !== h)
        : getDataOfCsvBasedOnHeaders(csvData, jobDetails)?.length > 0)
  }

  if (headersInvalid) {
    showError('Column headers are incorrect. Headers should be the same as the templates.')
    return false
  }

  let sequenceInvalid = false
  if (
    jobDetails?.attributes?.length === 1 &&
    [
      'STRUCTURED_CUE_POINTS',
      'STRUCTURED_AD_CUE_POINTS',
      'STRUCTURED_ADVISORY_CUE_POINTS',
    ].includes(jobDetails?.attributes[0]?.fieldName || '')
  ) {
    // Check for blank rows or empty Blitz ID fields
    const hasInvalidRows = csvData?.some(
      (item) => item[blitzKey] === null || item[blitzKey] === undefined || item[blitzKey] === '',
    )
    if (hasInvalidRows) {
      showError(
        'File contains blank rows or rows with an empty Blitz ID. Please fix and re-upload.',
      )
      return false
    }

    const closedIds = new Set()
    let currentId: any = null
    for (const row of csvDataFile) {
      const id = row[blitzKey]
      if (id !== currentId) {
        if (currentId !== null) closedIds.add(currentId)
        if (closedIds.has(id)) {
          sequenceInvalid = true
          break
        }
        currentId = id
      }
    }
  }

  if (sequenceInvalid) {
    showError('File upload failed because asset IDs are not in sequence')
    return false
  }

  // 5. File size validation
  if (bulkUpdateConfig?.maxFileSize < fileInfo?.size / 1024) {
    showError(CONSTANTS.UPLOAD_FAILED_FILE_SIZE_DESC(bulkUpdateConfig.maxFileSize))
    return false
  }

  // 6. Content size validation
  if (bulkUpdateConfig?.maxContentAllowed <= csvData?.length) {
    showError(CONSTANTS.UPLOAD_FAILED_CONTENT_DESC(bulkUpdateConfig.maxContentAllowed))
    return false
  }

  return true
}

export const removeSubAttributes = (attributes: TypeObject[]) => {
  const complexAttr = attributes.filter(
    (item) => item.fieldType === CONSTANTS.FIELDTYPES.COMPLEXTYPE,
  )
  const attRefMap = new Map()
  const attributeMap = new Map(attributes.map((item) => [item._id, item]))
  for (const attRef of complexAttr) {
    const fieldAttribute = attRef?.attributeRef ?? []
    for (const attRefField of fieldAttribute) {
      const field = attributes.find((item) => item?._id === attRefField?._id)
      if (field) {
        attRefMap.set(attRefField?._id, field)
      }
    }
  }

  for (const [key, value] of attRefMap) {
    if (attributeMap.has(key) && attributeMap.get(key) === value) {
      attributeMap.delete(key)
    }
  }

  return Array.from(attributeMap)
    .map((item) => item[1])
    .map((item) => item.fieldName)
}

export const isJobCancelledBeforeStart = (view: any) => {
  let jobCancelledBeforeStart = false
  if (
    view?.status === 'COMPLETED' &&
    view?.action === 'CANCELED' &&
    !(view?.failureCount >= 0 || view?.successCount >= 0)
  ) {
    jobCancelledBeforeStart = true
  }
  return jobCancelledBeforeStart
}

export const getInteractiveValueLabel = (title: string) => {
  switch (title) {
    case CONSTANTS.BULK_OPS.UPDATE_TYPE.OVERWRITE_WITH_NEW_VALUES:
      return CONSTANTS.BULK_OPS.INTERACTIVE_VALUE_LABEL.OVERWRITE_WITH_NEW_VALUES
    case CONSTANTS.BULK_OPS.UPDATE_TYPE.APPEND_TO_EXISTING_VALUES:
      return CONSTANTS.BULK_OPS.INTERACTIVE_VALUE_LABEL.APPEND_TO_EXISTING_VALUES
    case CONSTANTS.BULK_OPS.UPDATE_TYPE.REMOVE_SELECTED_VALUES:
      return CONSTANTS.BULK_OPS.INTERACTIVE_VALUE_LABEL.REMOVE_SELECTED_VALUES
    default:
      return title
  }
}

function makeCSVContent(csvRawData: any) {
  if (!csvRawData) throw new Error('No template data found')
  const escapeCsv = (field: any) => `"${String(field).replace(/"/g, '""')}"`
  const csvContent = [
    csvRawData.headers.map(escapeCsv).join(','),
    ...csvRawData.rows.map((r: any) => r.map(escapeCsv).join(',')),
  ].join('\n')
  return csvContent
}

export const downloadBulkUploadTemplate = async (
  dispatch: any,
  jobDetails: any,
  templateData: any,
) => {
  try {
    dispatch(setLoader(true))
    const { jobName = '', isLiveAssetJob = false, jobType = '' } = jobDetails || {}
    const safeJobName = sanitizeFileName(jobName)
    let csvContent: string | undefined
    if (jobType === CONFIG.BULK_OPS_TYPES.BULK_TRANSCODING) {
      const csvRawData = templateData?.integratedData
      csvContent = makeCSVContent(csvRawData)
    }
    if (isLiveAssetJob) {
      let csvRawData: any = []
      switch (jobDetails.attributes[0].key) {
        case 'SRT_FEED':
          csvRawData = templateData?.srtData
          break
        case 'ENCODED_FEED':
          csvRawData = templateData?.encodedData
          break
        case 'SRT_FEED_GEC':
          csvRawData = templateData?.srtGecData
          break
        case 'ENCODED_FEED_GEC':
          csvRawData = templateData?.encodedGecData
          break
      }
      csvContent = makeCSVContent(csvRawData)
    } else {
      if (jobDetails?.attributes) {
        const attributes = removeSubAttributes(jobDetails?.attributes)
        const reqData: any = API_CONFIG(API_LIST.BULK_UPDATE.DOWNLOD_CSV_DOWNLOAD, { attributes })
        const res: any = await mainApiService(reqData)
        if (res?.error) {
          dispatch(showAlert([true, combineErrorMessage(res), 'error']))
          return
        }
        csvContent = res
      }
    }
    if (!csvContent) throw new Error('Empty CSV content')
    let fileName = `BulkUpdateTemplate_${safeJobName}.csv`
    if (jobType === CONFIG.BULK_OPS_TYPES.BULK_TRANSCODING) {
      fileName = `Bulk_ReTranscoding_${safeJobName}.csv`
    }
    downloadDataInCSV(csvContent, fileName)
    dispatch(showAlert([true, CONSTANTS.TEMPLATE_DOWNLOAD_SUCCESS, 'success']))
  } catch (err) {
    dispatch(showAlert([true, 'Template download failed', 'error']))
  } finally {
    dispatch(setLoader(false))
  }
}
